import React, { useState } from 'react';
import Icon from '../AppIcon';
import Button from './Button';
import Input from './Input';

/**
 * Enhanced URL input component with bidirectional sync capabilities
 * Shows sync status, inheritance, and provides sync actions
 */
const SyncableUrlInput = ({
  platform,
  value,
  onChange,
  orgDefault = '',
  isOverride = false,
  source = 'none',
  onSyncToOrg,
  onSyncFromOrg,
  disabled = false,
  showSyncButtons = true,
  label,
  placeholder,
  className = ''
}) => {
  const [isLoading, setIsLoading] = useState(false);

  // Get platform display info
  const getPlatformInfo = () => {
    const configs = {
      google: { icon: 'G', color: 'blue', name: 'Google Business Profile' },
      yelp: { icon: 'Y', color: 'red', name: 'Yelp Business' },
      facebook: { icon: 'F', color: 'indigo', name: 'Facebook Page' }
    };
    return configs[platform] || { icon: '?', color: 'gray', name: platform };
  };

  const platformInfo = getPlatformInfo();

  // Handle sync actions
  const handleSyncToOrg = async () => {
    if (!onSyncToOrg || !value) return;
    
    setIsLoading(true);
    try {
      await onSyncToOrg(platform, value);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSyncFromOrg = async () => {
    if (!onSyncFromOrg) return;
    
    setIsLoading(true);
    try {
      await onSyncFromOrg(platform);
    } finally {
      setIsLoading(false);
    }
  };

  // Determine sync status
  const hasValue = !!value;
  const hasOrgDefault = !!orgDefault;
  const isDifferent = hasValue && hasOrgDefault && value !== orgDefault;
  const isInherited = !isOverride && hasOrgDefault;

  return (
    <div className={`space-y-3 ${className}`}>
      {/* Platform Header with Status */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          {/* Platform Icon */}
          <div className={`w-8 h-8 bg-${platformInfo.color}-600 rounded-full flex items-center justify-center`}>
            <span className="text-white text-sm font-medium">{platformInfo.icon}</span>
          </div>
          
          {/* Platform Name and Status */}
          <div>
            <div className="font-medium text-gray-900">
              {label || platformInfo.name}
            </div>
            <div className="flex items-center space-x-2 text-xs text-gray-500">
              {/* Source Indicator */}
              <span className={`px-2 py-1 rounded-full ${
                source === 'campaign' ? 'bg-purple-100 text-purple-700' :
                source === 'organization' ? 'bg-blue-100 text-blue-700' :
                'bg-gray-100 text-gray-600'
              }`}>
                {source === 'campaign' ? '📝 Campaign Override' :
                 source === 'organization' ? '🏢 Organization Default' :
                 '⚪ Not Set'}
              </span>
              
              {/* Sync Status */}
              {isDifferent && (
                <span className="px-2 py-1 rounded-full bg-yellow-100 text-yellow-700">
                  🔄 Different from default
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        {showSyncButtons && hasValue && hasOrgDefault && (
          <div className="flex items-center space-x-2">
            {isDifferent && (
              <>
                {/* Sync to Organization */}
                <button
                  onClick={handleSyncToOrg}
                  disabled={disabled || isLoading}
                  className="text-xs px-2 py-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded border border-blue-200 transition-colors"
                  title="Save as organization default"
                >
                  ⬆️ Save as default
                </button>
                
                {/* Sync from Organization */}
                <button
                  onClick={handleSyncFromOrg}
                  disabled={disabled || isLoading}
                  className="text-xs px-2 py-1 text-green-600 hover:text-green-800 hover:bg-green-50 rounded border border-green-200 transition-colors"
                  title="Use organization default"
                >
                  ⬇️ Use default
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {/* URL Input */}
      <div className="space-y-2">
        <Input
          type="url"
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          disabled={disabled}
          className={`${isOverride ? 'border-purple-300 bg-purple-50' : ''}`}
        />
        
        {/* Organization Default Display */}
        {hasOrgDefault && (isDifferent || !hasValue) && (
          <div className="text-xs text-gray-500 bg-gray-50 p-2 rounded border">
            <div className="flex items-center justify-between">
              <span>
                <strong>Organization default:</strong> {orgDefault}
              </span>
              {!hasValue && showSyncButtons && (
                <button
                  onClick={handleSyncFromOrg}
                  disabled={disabled || isLoading}
                  className="text-blue-600 hover:text-blue-800 underline"
                >
                  Use this
                </button>
              )}
            </div>
          </div>
        )}

        {/* Help Text */}
        {isInherited && (
          <div className="text-xs text-blue-600 bg-blue-50 p-2 rounded border border-blue-200">
            💡 This URL is inherited from your organization settings
          </div>
        )}
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center text-xs text-gray-500">
          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-gray-400 mr-2"></div>
          Syncing...
        </div>
      )}
    </div>
  );
};

/**
 * Bulk sync actions component for managing multiple URLs
 */
export const UrlSyncActions = ({
  hasDifferences = false,
  onSyncAllToOrg,
  onSyncAllFromOrg,
  disabled = false,
  className = ''
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleSyncAllToOrg = async () => {
    if (!onSyncAllToOrg) return;
    
    setIsLoading(true);
    try {
      await onSyncAllToOrg();
    } finally {
      setIsLoading(false);
    }
  };

  const handleSyncAllFromOrg = async () => {
    if (!onSyncAllFromOrg) return;
    
    setIsLoading(true);
    try {
      await onSyncAllFromOrg();
    } finally {
      setIsLoading(false);
    }
  };

  if (!hasDifferences) return null;

  return (
    <div className={`bg-yellow-50 border border-yellow-200 rounded-lg p-4 ${className}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Icon name="AlertTriangle" size={16} className="text-yellow-600" />
          <span className="text-sm font-medium text-yellow-800">
            URLs differ between campaign and organization settings
          </span>
        </div>
        
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleSyncAllFromOrg}
            disabled={disabled || isLoading}
            className="text-xs border-green-300 text-green-700 hover:bg-green-50"
          >
            ⬇️ Use all defaults
          </Button>
          
          <Button
            variant="outline" 
            size="sm"
            onClick={handleSyncAllToOrg}
            disabled={disabled || isLoading}
            className="text-xs border-blue-300 text-blue-700 hover:bg-blue-50"
          >
            ⬆️ Save all as defaults
          </Button>
        </div>
      </div>
      
      {isLoading && (
        <div className="mt-2 flex items-center text-xs text-yellow-700">
          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-yellow-600 mr-2"></div>
          Syncing all URLs...
        </div>
      )}
    </div>
  );
};

export default SyncableUrlInput;